import React from 'react';
import { css } from 'styled-components';
import RichTextElement from '../components/pageBuilder/Elements/RichText';
import CtaButtonRow from '../components/pageBuilder/Elements/CtaButtons';
import Header from '../components/pageBuilder/Elements/Header';
import SectionHero from '../components/pageBuilder/Sections/SectionHero';
import SectionTitledArticles from '../components/pageBuilder/Sections/SectionTitledArticles';
import SectionFeatureCards from '../components/pageBuilder/Sections/SectionFeatureCards';
import SectionForm from '../components/pageBuilder/Sections/SectionForm';
import SectionFilterableArticles from '../components/pageBuilder/Sections/SectionFilterableArticles';
import SectionInfo from '../components/pageBuilder/Sections/SectionInfo';
import { SectionFeature } from '../components/pageBuilder/Sections/SectionFeature';
import { SectionFeatureRow } from '../components/pageBuilder/Sections/SectionFeature';
import SectionItemLinkBar from '../components/pageBuilder/Sections/SectionIconLinkBar';
import SectionBreadcrumb from '../components/pageBuilder/Sections/SectionBreadcrumb';
import SectionStepper from '../components/pageBuilder/Sections/SectionStepper';
import SectionAccordion from '../components/pageBuilder/Sections/SectionAccordion';
import _ from 'lodash';
import Icon from '@vfuk/core-icon';
import  {SectionSmartSearchMain , SectionSmartSearchResult} from '../components/pageBuilder/Sections/SectionSmartSearch';
import  { SectionAssessmentAboutBusiness, SectionAssessmentBusinessGoals, SectionAssessmentSummary } from '../components/pageBuilder/Sections/SectionAssessment';
import SectionCustomStepper from '../components/pageBuilder/Sections/SectionCustomStepper/SectionCustomStepper';
import SimpleGrid from '@vfuk/core-simple-grid';
import Block from '@vfuk/core-block';
import {
  springAndRedCSS,
  springAndTransparentCSS,
  transparentCSS,
  whiteCSS
} from "./page-builder-helpers.styles";
import Spacing from '@vfuk/core-spacing';

const CONTENTFUL_SECTION_FILTERABLE_ARTICLE_INDEX =
  'ContentfulSectionFilterableArticleIndex';
const CONTENTFUL_SECTION_CTA = 'ContentfulSectionCta';
const CONTENTFUL_SECTION_ELEMENT_RICH_TEXT = 'ContentfulSectionElementRichText';
const CONTENTFUL_SECTION_ELEMENT_BUTTON_ROW =
  'ContentfulSectionElementCtaButtonRow';
const CONTENTFUL_SECTION_HERO_WITH_TEXT_AND_CTA =
  'ContentfulSectionHeroWithTextAndCta';
const CONTENTFUL_SECTION_FEATURE_CARD_ROW = 'ContentfulSectionFeatureCardRow';
const CONTENTFUL_SECTION_TITLED_ARTICLE_INDEX_LIST =
  'ContentfulSectionTitledArticleIndexList';
const CONTENTFUL_SECTION_FORM = 'ContentfulSectionForm';
const CONTENTFUL_SECTION_INFO = 'ContentfulSectionInfo';
const CONTENTFUL_SECTION_SEARCH_MAIN_PAGE = 'ContentfulSectionSearchMainPage';
const CONTENTFUL_SECTION_SEARCH_RESULT_PAGE = 'ContentfulSectionSearchResultPage';
const CONTENTFUL_SECTION_ASSESSMENT_ABOUT_BUSINESS = 'ContentfulSectionAssessmentAboutBusiness';
const CONTENTFUL_SECTION_ASSESSMENT_BUSINESS_GOALS = 'ContentfulSectionAssessmentBusinessGoals';
const CONTENTFUL_SECTION_ASSESSMENT_SUMMARY_PAGE = 'ContentfulSectionAssessmentSummaryPage';
const CONTENTFUL_SECTION_FEATURE = 'ContentfulSectionFeature';
const CONTENTFUL_SECTION_FEATURE_ROW = 'ContentfulSectionFeatureRow';
const CONTENTFUL_SECTION_ICON_LINK_BAR = 'ContentfulSectionIconLinkBar';
const CONTENTFUL_SECTION_BREADCRUMB = 'ContentfulSectionBreadcrumb';
const CONTENTFUL_SECTION_STEPPER = 'ContentfulSectionStepper';
const CONTENTFUL_SECTION_ACCORDION = 'ContentfulSectionAccordion';
const CONTENTFUL_SECTION_CUSTOM_STEPPER = 'ContentfulSectionCustomStepper';

const makePageSections = (sections, recaptchaToken, location, seoTitle, nodeLocale) => {
  const pageSections = sections.map(section => {
    let sectionContent, sectionElementIconSvg, sectionBeforeId;

    switch (section.__typename) {
      case CONTENTFUL_SECTION_INFO:
        sectionBeforeId = <SectionInfo key={section.id} data={section} />;
        break;

      case CONTENTFUL_SECTION_SEARCH_MAIN_PAGE:
        sectionBeforeId = <SectionSmartSearchMain key={section.id} content={section}/>;
        break;

      case CONTENTFUL_SECTION_SEARCH_RESULT_PAGE:
        sectionBeforeId = <SectionSmartSearchResult key={section.id} content={section}/>;
        break;

      case CONTENTFUL_SECTION_ASSESSMENT_ABOUT_BUSINESS:
        sectionBeforeId = <SectionAssessmentAboutBusiness key={section.id} content={section}/>;
        break;

      case CONTENTFUL_SECTION_ASSESSMENT_BUSINESS_GOALS:
        sectionBeforeId = <SectionAssessmentBusinessGoals key={section.id} content={section}/>;
        break;

      case CONTENTFUL_SECTION_ASSESSMENT_SUMMARY_PAGE:
        sectionBeforeId = <SectionAssessmentSummary key={section.id} content={section}/>;
        break;

      case CONTENTFUL_SECTION_FEATURE:
        sectionBeforeId = <SectionFeature key={section.id} data={section} />;
        break;

      case CONTENTFUL_SECTION_FEATURE_ROW: {
        const { backgroundColor } = section;
        const featureImage = section.content.find(
          sectionItem =>
            sectionItem.__typename === 'ContentfulSectionElementFeatureImage'
        );

        const featureHeader = section.content.find(
          sectionItem =>
            sectionItem.__typename === 'ContentfulSectionElementHeader'
        );

        const featureItems = section.content.filter(
          sectionItem =>
            sectionItem.__typename === 'ContentfulSectionElementFeatureItem'
        );

        const featureButtons = section.content.find(
          sectionItem =>
            sectionItem.__typename === 'ContentfulSectionElementCtaButtonRow'
        );

        sectionBeforeId = (
          <SectionFeatureRow
            key={section.id}
            backgroundColor={backgroundColor}
            featureImage={featureImage}
            featureHeader={featureHeader}
            featureItems={featureItems}
            featureButtons={featureButtons}
          />
        );
        break;
      }

      case CONTENTFUL_SECTION_FILTERABLE_ARTICLE_INDEX:
        sectionBeforeId = (
          <SectionFilterableArticles
            key={section.id}
            section={section}
            location={location}
          />
        );
        break;

      case CONTENTFUL_SECTION_CTA:
        // Builds a Call to Action section, consisting of one or more
        // button rows and explanatory rich text.
        sectionContent = makeCTAContent(section.content);
        sectionBeforeId = (
              <Block className="spring spring--md" css={springAndTransparentCSS} key={section.id} >
                <Spacing spacingLevel={{
                  bottom: 10,
                  top: 6,
                }}>{sectionContent}</Spacing>
              </Block>
        );
        break;

      case CONTENTFUL_SECTION_FEATURE_CARD_ROW:
        sectionContent = section.content.map(sectionItem => {
          return (
            <SimpleGrid
              columns={{
                sm: 1,
                md: 1,
                lg: 2
              }}
              spacing={3}
              key={sectionItem.id}
              justify={'center'}
            >
              <SectionFeatureCards content={sectionItem} />
            </SimpleGrid>
          );
        });
        sectionBeforeId = (
          <Block key={section.id} appearance="alt2">
            {sectionContent}
          </Block>
        );
        break;

      case CONTENTFUL_SECTION_HERO_WITH_TEXT_AND_CTA: {
        // Builds a Hero image with optional text and CTA buttons.
        const {
          id,
          responsiveImageCollection,
          textPosition,
          mirrorContent,
          content,
        } = section;
        sectionBeforeId = (
          <SectionHero
            key={id}
            //TODO: Remove mainImage from Contentful model: Section - Hero with text and CTA
            // mainImage={section.mainImage}
            responsiveImageCollection={responsiveImageCollection}
            textPosition={textPosition}
            content={content}
            mirrorContent={mirrorContent}
          />
        );
        break;
      }

      case CONTENTFUL_SECTION_TITLED_ARTICLE_INDEX_LIST:
        sectionBeforeId = section.content.map((sectionItem, index) => {
          const { id, content } = sectionItem;
          const backgroundColor = index % 2 === 0 ? 'gallery' : 'white';
          const backgroundColorCss = index % 2 === 0 ? transparentCSS : whiteCSS;

          return (
            <Block
              key={id}
              css={backgroundColorCss}
            >
              <div className="spring">
                <SectionTitledArticles
                  key={id}
                  slug={section.slug}
                  content={content}
                  backgroundColor={backgroundColor}
                  seoTitle={seoTitle}
                />
              </div>
            </Block>
          );
        });
        break;

      case CONTENTFUL_SECTION_FORM: {
        // Build a Form section

        // Form Input Fields
        const sectionElementInputs = section.content.filter(
          sectionItem =>
            sectionItem.__typename !== 'ContentfulSectionElementHeader'
        );

        // IconSvg Headers
        const sectionElementIcon = section.content.find(
          sectionItem =>
            sectionItem.__typename === 'ContentfulSectionIconLinkBar'
        );

        if (sectionElementIcon !== undefined) {
          sectionElementIconSvg = sectionElementIcon?.icons?.map(icon => {
            return (
              <div
                key={icon.id}
                className="IconSvg-wrapper"
                css={css`
                  color: ${icon.color};
                  text-align: center;
                  margin-bottom: 30px;
                  margin-top: 30px;
                `}
              >
                <Icon key={icon.id} name={icon.iconRef} size={5} group='midLight' />
              </div>
            );
          });
        }

        // Form Headers
        const sectionElementHeaders = section.content
          .filter(
            sectionItem =>
              sectionItem.__typename === 'ContentfulSectionElementHeader'
          )
          .map(sectionItem => {
            const { id, ...content } = sectionItem;
            return <Header key={id} content={content} />;
          });

        //  Rich Text
        const sectionElementRichText = section.content.find(
          sectionItem =>
            sectionItem.__typename === 'ContentfulSectionElementRichText'
        );

        const sectionFormProps = {
          nodeLocale,
          formInputs: sectionElementInputs,
          richText: sectionElementRichText,
          token: recaptchaToken,
          entryId: section.contentful_id,
          slug: section.slug,
          seoEventData: section.seoEventData.seoEventData,
          operatingDays: section.operatingDays,
          datePickerTitle: section.datePickerTitle,
          datePickerPlaceholderText: section.datePickerPlaceholderText,
          datePickerDateFormat: section.datePickerDateFormat,
          timePickerTitle: section.timePickerTitle,
          timePickerPlaceholderText: section.timePickerPlaceholderText,
          timePickerCaption: section.timePickerCaption,
          optionalFieldIndicatorText: section.optionalFieldIndicatorText,
          datePickerTimePickerSeparatorText: section.datePickerTimePickerSeparatorText,
          serverErrorMessage: section.serverErrorMessage
        };

        sectionBeforeId = (
          <Block paddingLevel={2} key={section.id} css={whiteCSS}>
              <div className="spring">
                {sectionElementIconSvg}
                {sectionElementHeaders}
                <SectionForm {...sectionFormProps} />
              </div>
          </Block>
        );
        break;
      }

      case CONTENTFUL_SECTION_ELEMENT_BUTTON_ROW:
        sectionBeforeId = (
          <Block key={section.id} css={transparentCSS}>
            <Spacing spacingLevel={{
              right: 5,
              left: 5
            }}>
              <div
                css={css`
                  margin-bottom: 30px;
                  padding: 0px 18px;
                `}
              >
                <CtaButtonRow
                  key={section.id}
                  buttonRowAlignment={section.buttonRowAlignment}
                  buttons={section.buttons}
                />
              </div>
            </Spacing>
          </Block>
        );
        break;

      case CONTENTFUL_SECTION_ICON_LINK_BAR:
        sectionBeforeId = (
          <Block
            key={section.id}
            css={springAndRedCSS}
          >
            <SectionItemLinkBar key={section.id} data={section} />
          </Block>
        );
        break;

      case CONTENTFUL_SECTION_BREADCRUMB: {
        const { id, content } = section;
        sectionBeforeId = (
          <SectionBreadcrumb key={id} items={content} location={location} />
        );
        break;
      }

      case CONTENTFUL_SECTION_STEPPER: {
        const { id, content } = section;
        sectionBeforeId = (
          <SectionStepper key={id} items={content} location={location} />
        );
        break;
      }

      case CONTENTFUL_SECTION_ACCORDION: {
        sectionBeforeId = <SectionAccordion key={section.id} section={section} />;
        break;
      }

      case CONTENTFUL_SECTION_CUSTOM_STEPPER: {
        const { id, title, text, content, buttonRow } = section;
        sectionBeforeId = (
          <SectionCustomStepper
            key={id}
            title={title}
            text={text}
            content={content}
            buttons={buttonRow.buttons}
            buttonRowAlignment={buttonRow.buttonRowAlignment}
          />
        );
        break;
      }

      case CONTENTFUL_SECTION_CUSTOM_STEPPER: {
        const { id, title, text, content, buttonRow } = section;
        return (
          <SectionCustomStepper
            key={id}
            title={title}
            text={text}
            content={content}
            buttons={buttonRow.buttons}
            buttonRowAlignment={buttonRow.buttonRowAlignment}
          />
        );
      }

      default:
        return false;
    }

    return <div id={section.name}>{sectionBeforeId}</div>
  });
  return pageSections;
};

const makeCTAContent = content => {
  // Builds the content for a CONTENTFUL_SECTION_CTA.
  const ctaContent = content.map(content => {
    switch (content.__typename) {
      case CONTENTFUL_SECTION_ELEMENT_BUTTON_ROW:
        return (
          <CtaButtonRow
            key={content.id}
            buttonRowAlignment={content.buttonRowAlignment}
            buttons={content.buttons}
          />
        );

      case CONTENTFUL_SECTION_ELEMENT_RICH_TEXT:
        return (
          <RichTextElement
            key={content.id}
            className="richText"
            rawRichText={content.text.raw}
            text={content.text}
            color={content.color}
            textAlign={content.textAlign}
          />
        );

      default:
        return false;
    }
  });
  return ctaContent;
};

export default makePageSections;
export { makeCTAContent };